import React, { useState, useEffect } from 'react';

const BG = '#0a0a0c';
const PANEL = '#14141a';
const ACCENT = '#f0a800';
const TEXT = '#faf9f5';
const MUTED = 'rgba(250,249,245,0.55)';
const FAINT = 'rgba(250,249,245,0.35)';
const BORDER = 'rgba(250,249,245,0.12)';

export function Login({ onSuccess }) {
  const [stage, setStage] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => {
      const left = Math.max(0, Math.floor(expiresAt - Date.now() / 1000));
      setCountdown(left);
      if (left === 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  async function requestCode(e) {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await fetch('/api/login/request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'não rolou mandar o código');
      setExpiresAt(data.expires_at);
      setStage('code');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e) {
    e?.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await fetch('/api/login/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'código não bateu');
      onSuccess?.(data.user);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const cardStyle = {
    background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 16,
    padding: 32, width: '100%', maxWidth: 420,
    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
  };
  const inputStyle = {
    width: '100%', padding: '12px 14px', background: BG, color: TEXT,
    border: `1px solid ${BORDER}`, borderRadius: 8,
    fontFamily: 'inherit', fontSize: 14, outline: 'none',
    boxSizing: 'border-box',
  };
  const codeInputStyle = {
    ...inputStyle, fontFamily: 'ui-monospace,Menlo,monospace',
    fontSize: 24, letterSpacing: '0.3em', textAlign: 'center',
    color: ACCENT,
  };
  const btnStyle = {
    width: '100%', padding: '12px 20px', background: ACCENT, color: BG,
    border: 0, borderRadius: 8, fontWeight: 600, fontSize: 14,
    cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
    transition: 'opacity 0.15s',
  };

  return (
    <div style={{
      minHeight: '100vh', background: BG, color: TEXT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', padding: 20, gap: 32,
      fontFamily: "'Geist',-apple-system,BlinkMacSystemFont,sans-serif",
    }}>
      <div style={{ textAlign: 'center' }}>
        <svg width="80" height="80" viewBox="0 0 400 400" style={{ display: 'block', margin: '0 auto 16px' }}>
          <circle cx="200" cy="200" r="80" fill="none" stroke={ACCENT} strokeWidth="3" opacity="0.4"/>
          <circle cx="200" cy="200" r="50" fill="none" stroke={ACCENT} strokeWidth="2.5"/>
          <line x1="200" y1="156" x2="200" y2="188" stroke={ACCENT} strokeWidth="4" strokeLinecap="round"/>
          <circle cx="200" cy="200" r="6" fill={ACCENT}/>
        </svg>
        <div style={{
          color: ACCENT, fontFamily: 'ui-monospace,Menlo,monospace',
          fontSize: 14, letterSpacing: '0.3em', fontWeight: 600,
        }}>FLUXO</div>
        <div style={{
          color: MUTED, fontFamily: 'ui-monospace,Menlo,monospace',
          fontSize: 10, letterSpacing: '0.25em', marginTop: 4,
        }}>O QUE MOVE O NORTE</div>
      </div>

      <div style={cardStyle}>
        {stage === 'email' && (
          <form onSubmit={requestCode}>
            <label style={{ display: 'block', fontSize: 13, color: MUTED, marginBottom: 8 }}>
              Teu email PLX
            </label>
            <input
              type="email"
              autoFocus
              required
              placeholder="voce@playloadx.ai"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: MUTED, marginTop: 8, marginBottom: 20, lineHeight: 1.5 }}>
              Só <code style={{ color: ACCENT }}>@playloadx.ai</code>. Mandamos um código no email — cola aqui e tu entra.
            </div>
            <button type="submit" disabled={busy || !email} style={btnStyle}>
              {busy ? 'Mandando…' : 'Mandar código'}
            </button>
          </form>
        )}

        {stage === 'code' && (
          <form onSubmit={verifyCode}>
            <label style={{ display: 'block', fontSize: 13, color: MUTED, marginBottom: 8 }}>
              Código em <span style={{ color: TEXT }}>{email}</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={busy}
              style={codeInputStyle}
            />
            <div style={{ fontSize: 11, color: MUTED, marginTop: 8, marginBottom: 20, display: 'flex', justifyContent: 'space-between' }}>
              <span>{countdown > 0 ? `expira em ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}` : 'expirou — pede outro'}</span>
              <button
                type="button"
                onClick={() => { setStage('email'); setCode(''); setErr(null); }}
                style={{ background: 'transparent', border: 0, color: ACCENT, cursor: 'pointer', fontSize: 11, padding: 0 }}
              >
                outro email
              </button>
            </div>
            <button type="submit" disabled={busy || code.length !== 6} style={btnStyle}>
              {busy ? 'Entrando…' : 'Entrar no Fluxo →'}
            </button>
          </form>
        )}

        {err && (
          <div style={{
            marginTop: 16, padding: '10px 14px', background: 'rgba(255,100,100,0.1)',
            border: '1px solid rgba(255,100,100,0.3)', color: '#ff8a80',
            borderRadius: 8, fontSize: 12,
          }}>
            {err}
          </div>
        )}
      </div>

      <div style={{ fontSize: 10, color: FAINT, letterSpacing: '0.2em' }}>
        PLX EMPIRE
      </div>
    </div>
  );
}
